/**
 * VAT rate determination for passenger transport.
 *
 * The rule that matters: the reduced 7 % rate in § 12 Abs. 2 Nr. 10 Buchst. b
 * UStG applies to an enumerated list of transport modes. "Verkehr mit Taxen"
 * on that list means Taxenverkehr under § 47 PBefG. A limousine service
 * operating as Mietwagen under § 49 PBefG is not on the list and charges the
 * standard rate — including on a five-kilometre inner-city ride. The BFH has
 * held this repeatedly and the BVerfG has confirmed it: Mietwagen are not
 * public transport, carry neither Betriebs- nor Beförderungspflicht, and are
 * not tariff-bound.
 *
 * So the rate is keyed to the licence type first, and only then to distance.
 * Deriving it from distance alone would systematically understate VAT for the
 * operator this app is built for.
 *
 * See `01_Input/rechtliche-anforderungen.md` section 9.
 */

import type { CompanySettings, LicenceType } from './types';

export const VAT_STANDARD = 19;
export const VAT_REDUCED = 7;
export const VAT_EXEMPT = 0;

/**
 * § 12 Abs. 2 Nr. 10 b bb UStG — the reduced rate ceiling for a single
 * journey, in metres.
 */
export const SHORT_DISTANCE_LIMIT_METERS = 50_000;

export interface VatContext {
  licenceType: LicenceType;
  kleinunternehmer: boolean;
  distanceMeters: number;
  /** True when the whole journey stays inside one municipality. */
  withinSingleMunicipality?: boolean;
  /**
   * A Krankenfahrt performed under a Sondervereinbarung with the health
   * insurers that is materially equivalent to the agreement applying to
   * Taxiunternehmer, in the same geographic area. This is the one narrow route
   * by which a Mietwagen operator reaches the reduced rate, and the operator
   * must be able to prove the conditions for each individual transaction.
   */
  krankenfahrtSondervereinbarung?: boolean;
}

export interface VatDecision {
  ratePercent: number;
  /** Shown next to the rate in the UI so the driver can see the reasoning. */
  rationale: string;
  /** Statutory citation for the export and the invoice footnote. */
  legalBasis: string;
  /** Set when the operator should be able to produce evidence on request. */
  requiresEvidence: boolean;
}

export function determineVatRate(context: VatContext): VatDecision {
  if (context.kleinunternehmer) {
    return {
      ratePercent: VAT_EXEMPT,
      rationale: 'Kleinunternehmerregelung — keine Umsatzsteuer ausgewiesen.',
      legalBasis: '§ 19 UStG',
      requiresEvidence: false,
    };
  }

  const isShortDistance =
    context.withinSingleMunicipality === true ||
    context.distanceMeters <= SHORT_DISTANCE_LIMIT_METERS;

  if (context.licenceType === 'taxi') {
    if (isShortDistance) {
      return {
        ratePercent: VAT_REDUCED,
        rationale: context.withinSingleMunicipality
          ? 'Taxenverkehr innerhalb einer Gemeinde.'
          : 'Taxenverkehr, Beförderungsstrecke bis 50 km.',
        legalBasis: '§ 12 Abs. 2 Nr. 10 Buchst. b UStG',
        requiresEvidence: false,
      };
    }
    return {
      ratePercent: VAT_STANDARD,
      rationale: 'Taxenverkehr, Beförderungsstrecke über 50 km.',
      legalBasis: '§ 12 Abs. 1 UStG',
      requiresEvidence: false,
    };
  }

  if (context.krankenfahrtSondervereinbarung && isShortDistance) {
    return {
      ratePercent: VAT_REDUCED,
      rationale:
        'Krankenfahrt aufgrund Sondervereinbarung mit den Krankenkassen. ' +
        'Nachweis je Einzelfall erforderlich.',
      legalBasis: '§ 12 Abs. 2 Nr. 10 Buchst. b UStG',
      requiresEvidence: true,
    };
  }

  return {
    ratePercent: VAT_STANDARD,
    rationale:
      'Mietwagenverkehr nach § 49 PBefG ist nicht begünstigt — der ermäßigte ' +
      'Satz gilt nur für Taxen nach § 47 PBefG, unabhängig von der Entfernung.',
    legalBasis: '§ 12 Abs. 1 UStG',
    requiresEvidence: false,
  };
}

/** Convenience wrapper for the common case of a trip under current settings. */
export function vatRateForTrip(
  company: Pick<CompanySettings, 'licenceType' | 'kleinunternehmer'>,
  distanceMeters: number,
  options?: Pick<VatContext, 'withinSingleMunicipality' | 'krankenfahrtSondervereinbarung'>,
): VatDecision {
  return determineVatRate({
    licenceType: company.licenceType,
    kleinunternehmer: company.kleinunternehmer,
    distanceMeters,
    ...options,
  });
}

/**
 * The exemption note that must appear on the invoice when no VAT is shown.
 * § 14 Abs. 4 Nr. 8 UStG requires a reference to the exemption; for § 19 the
 * content follows § 34a UStDV.
 */
export function exemptionNote(ratePercent: number, kleinunternehmer: boolean): string | null {
  if (ratePercent > 0) return null;
  if (kleinunternehmer) {
    return 'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.';
  }
  return 'Steuerfreie Leistung.';
}

/**
 * EN 16931 VAT category code. `S` standard, `E` exempt, `Z` zero-rated.
 * Kleinunternehmer maps to `E` with the § 19 reason, not to `Z`.
 */
export function vatCategoryCode(ratePercent: number, kleinunternehmer: boolean): 'S' | 'E' | 'Z' {
  if (ratePercent > 0) return 'S';
  return kleinunternehmer ? 'E' : 'Z';
}
